import React, { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, MessageCircle, Music2, Send } from "lucide-react";
import { api } from "../lib/api";

function Page({ eyebrow, title, children }) {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
      <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#306D29] mb-3">{eyebrow}</p>
      <h1 className="font-display text-5xl font-light tracking-tight text-[#0D530E] mb-10">{title}</h1>
      <div className="space-y-8 text-[#306D29]/85 font-light leading-relaxed">{children}</div>
    </div>
  );
}
function H({ children }) { return <h2 className="font-display text-2xl text-[#0D530E] mb-2">{children}</h2>; }
function Block({ q, children }) { return <div><H>{q}</H><div className="space-y-2">{children}</div></div>; }

export function FAQs() {
  return (
    <Page eyebrow="Help" title="Frequently asked questions">
      <Block q="What is your service?"><p>We help you access trusted products from the UK and have them delivered to you in Ghana. We handle sourcing, purchasing, shipping, customs and delivery — all included in one transparent price.</p></Block>
      <Block q="How does ordering work?"><p>1. Browse products. 2. Add to basket. 3. Pay. 4. We purchase from approved UK stores. 5. Your order is packed and shipped. 6. You receive updates until delivery.</p></Block>
      <Block q="How long does delivery take?"><p>Delivery from the UK to Ghana typically takes <b>4–6 weeks</b>, depending on your location and customs processing. You'll receive a tracking number once your order ships so you can monitor progress.</p></Block>
      <Block q="What if my order arrives damaged?"><p>We pack everything with care, but if anything arrives damaged, contact us within <b>48 hours</b> with photos. We'll arrange a replacement or full refund — no questions asked.</p></Block>
      <Block q="How are my items packaged?"><p>We use durable, secure packaging to ensure your groceries and products arrive in perfect condition.</p></Block>
      <Block q="Do I have to pay customs or import fees?"><p>No. Customs duties, taxes and import handling are already included in the price you pay at checkout. There is nothing extra to pay on delivery.</p></Block>
      <Block q="Can I order products that are not listed?"><p>Yes. Use “Request a Product” and we'll confirm availability and pricing from UK retailers.</p></Block>
      <Block q="What currency can I pay with?"><p>Prices are shown in GH₵ (with a GBP reference). You're charged in GBP at checkout, secured by Stripe. More local payment options are coming.</p></Block>
      <Block q="Are products authentic?"><p>Yes. We source only from trusted UK retailers and suppliers.</p></Block>
      <Block q="Can I cancel my order?"><p>Orders can be cancelled before purchasing has started. Once products have been purchased, cancellation may not be possible.</p></Block>
    </Page>
  );
}

export function Shipping() {
  return (
    <Page eyebrow="Policy" title="Shipping policy">
      <p>We are committed to reliable international delivery of authentic UK products.</p>
      <Block q="Order processing"><p>After payment confirmation, we begin sourcing your products. Processing usually takes 1–3 business days. Some products may require additional sourcing time.</p></Block>
      <Block q="Packaging"><p>We use durable, secure packaging to ensure your groceries and products arrive in perfect condition.</p></Block>
      <Block q="Shipping methods"><p>We consolidate and ship orders from the UK using trusted international partners (air and sea freight and courier delivery).</p></Block>
      <Block q="Delivery times"><p>Delivery to Ghana typically takes <b>4–6 weeks</b>, depending on location and customs processing. Times may vary during public holidays or carrier delays.</p></Block>
      <Block q="Customs & import charges"><p>All customs duties, taxes and import fees are <b>included in the price you pay at checkout</b>. You will never be asked to pay extra customs charges on delivery.</p></Block>
      <Block q="Order tracking"><p>You receive email updates throughout — order received, purchased, shipped (with a tracking number), customs cleared, out for delivery and delivered.</p></Block>
    </Page>
  );
}

export function Returns() {
  return (
    <Page eyebrow="Policy" title="Returns & refund policy">
      <p>Because many products are purchased specifically for each customer, return eligibility depends on the product category.</p>
      <Block q="Damaged or incorrect items"><p>If anything arrives damaged, incorrect or with a manufacturing defect, contact us within <b>48 hours</b> of delivery with your order number and photos. We'll arrange a replacement or full refund — no questions asked.</p></Block>
      <Block q="Non-returnable items"><p>For safety and hygiene, some items cannot be returned: opened food, personal care, baby products, supplements, and beauty products.</p></Block>
      <Block q="Refunds"><p>Approved refunds are processed after review. Timelines depend on the original payment method.</p></Block>
    </Page>
  );
}

export function Privacy() {
  return (
    <Page eyebrow="Policy" title="Privacy policy">
      <p>We collect only the information necessary to process orders, provide customer support, and improve our services.</p>
      <Block q="Information we collect"><p>Account details, payment information, delivery information, and communication preferences.</p></Block>
      <Block q="How we use it"><p>To process and deliver orders, support you, and improve our platform. We do not sell your personal data.</p></Block>
      <Block q="Data protection & third parties"><p>We use trusted third-party services (e.g. payment and shipping partners) only as needed to fulfil your order, and take reasonable measures to protect your data.</p></Block>
    </Page>
  );
}

export function Terms() {
  return (
    <Page eyebrow="Legal" title="Terms & conditions">
      <Block q="Service agreement"><p>By using our platform, you agree to these terms.</p></Block>
      <Block q="Product availability"><p>Products are subject to availability from UK suppliers.</p></Block>
      <Block q="Pricing"><p>Prices may change due to supplier pricing, currency changes, or shipping costs. Customs and import fees are included in the price shown at checkout.</p></Block>
      <Block q="Delivery"><p>Delivery timelines are estimates (typically 4–6 weeks) and may be affected by external factors.</p></Block>
      <Block q="Product restrictions"><p>Some products may not be available for international shipment due to regulations.</p></Block>
      <Block q="Liability"><p>We are responsible for sourcing and coordinating delivery but cannot control delays caused by customs, carriers, or government authorities.</p></Block>
    </Page>
  );
}

const SOCIALS = [
  { icon: Music2, label: "TikTok", href: "https://www.tiktok.com/@afrigate" },
  { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/afrigate" },
  { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/afrigate" },
  { icon: MessageCircle, label: "WhatsApp", href: "https://wa.me/447512914627" },
];

function ContactForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/contact", f);
      toast.success("Message sent — we'll get back to you shortly.");
      setF({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error("Could not send message. Please email support@afrigateuk.com."); }
    finally { setBusy(false); }
  };
  const input = "w-full bg-white border border-[#0D530E]/15 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#306D29] focus:outline-none";
  return (
    <form onSubmit={submit} className="bg-white border border-[#0D530E]/10 rounded-2xl p-6 space-y-3 not-prose" data-testid="contact-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input required className={input} placeholder="Your name" value={f.name} onChange={set("name")} data-testid="contact-name" />
        <input required type="email" className={input} placeholder="Email" value={f.email} onChange={set("email")} data-testid="contact-email" />
      </div>
      <input className={input} placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} data-testid="contact-phone" />
      <textarea required rows={4} className={input} placeholder="How can we help?" value={f.message} onChange={set("message")} data-testid="contact-message" />
      <button type="submit" disabled={busy} data-testid="contact-submit"
        className="inline-flex items-center gap-2 bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold px-6 py-3 rounded-full disabled:opacity-60"
        style={{ transition: "background-color 0.2s ease" }}>
        <Send size={16} /> {busy ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}

export function Support() {
  return (
    <Page eyebrow="We're here to help" title="Help & support">
      <p>Have a question about an order, a product, or a box? Send us a message and we'll get back to you.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 not-prose">
        <ContactForm />
        <div className="space-y-5">
          <div className="flex items-start gap-3"><MapPin size={20} className="text-[#306D29] mt-0.5 shrink-0" /><div><p className="font-semibold text-[#0D530E]">Location</p><p className="text-[#306D29]/85">Leeds, United Kingdom</p></div></div>
          <div className="flex items-start gap-3"><Phone size={20} className="text-[#306D29] mt-0.5 shrink-0" /><div><p className="font-semibold text-[#0D530E]">Phone</p><a href="tel:+447512914627" className="text-[#306D29]/85 hover:text-[#0D530E]">+44 7512 914627</a></div></div>
          <div className="flex items-start gap-3"><Mail size={20} className="text-[#306D29] mt-0.5 shrink-0" /><div><p className="font-semibold text-[#0D530E]">Email</p><a href="mailto:support@afrigateuk.com" className="text-[#306D29]/85 hover:text-[#0D530E]">support@afrigateuk.com</a></div></div>
          <div className="flex items-start gap-3"><Clock size={20} className="text-[#306D29] mt-0.5 shrink-0" /><div><p className="font-semibold text-[#0D530E]">Working hours</p><p className="text-[#306D29]/85">Mon – Fri: 9:00 AM – 5:00 PM</p></div></div>
          <div>
            <p className="font-semibold text-[#0D530E] mb-2">Follow us</p>
            <div className="flex gap-2.5">
              {SOCIALS.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} data-testid={`social-${s.label.toLowerCase()}`}
                  className="w-10 h-10 rounded-full bg-[#0D530E] text-[#FBF5DD] flex items-center justify-center hover:bg-[#306D29]" style={{ transition: "background-color 0.2s ease" }}>
                  <s.icon size={18} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Block q="Track your order"><p>Use the <a href="/track" className="text-[#306D29] underline">Track Order</a> page with your order number for live status.</p></Block>
      <Block q="Quick links"><p><a href="/faqs" className="text-[#306D29] underline">FAQs</a> · <a href="/shipping" className="text-[#306D29] underline">Shipping</a> · <a href="/returns" className="text-[#306D29] underline">Returns</a> · <a href="/how-it-works" className="text-[#306D29] underline">How it works</a></p></Block>
    </Page>
  );
}
